import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const VARIANT_PRODUCT_TYPES = ['champion', 'skins', 'rank', 'unranked'];

const VARIANT_DEFINITIONS = {
	champion: [
		{ label: 'Hasta 20', value: '0-20' },
		{ label: '20-50', value: '20-50' },
		{ label: '50-100', value: '50-100' },
		{ label: '100-165', value: '100-165' },
	],
	skins: [
		{ label: 'Hasta 20', value: '0-20' },
		{ label: '20-50', value: '20-50' },
		{ label: '50-100', value: '50-100' },
		{ label: '100-200', value: '100-200' },
		{ label: '200-300', value: '200-300' },
		{ label: '300-500', value: '300-500' },
	],
	rank: [
		{ label: 'Hierro', value: 'hierro' },
		{ label: 'Bronce', value: 'bronce' },
		{ label: 'Plata', value: 'plata' },
		{ label: 'Oro', value: 'oro' },
		{ label: 'Platino', value: 'platino' },
		{ label: 'Esmeralda', value: 'esmeralda' },
		{ label: 'Diamante', value: 'diamante' },
		{ label: 'Master', value: 'master' },
		{ label: 'Grandmaster', value: 'grandmaster' },
		{ label: 'Challenger', value: 'challenger' },
	],
	unranked: [
		{ label: '40k BE', value: '40k' },
		{ label: '50k BE', value: '50k' },
		{ label: '60k BE', value: '60k' },
		{ label: '70k BE', value: '70k' },
	],
};

const AdminPanel = () => {
	const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [loginError, setLoginError] = useState('');
	const [loginLoading, setLoginLoading] = useState(false);

	const [activeTab, setActiveTab] = useState('products');
	const [error, setError] = useState('');
	const [successMsg, setSuccessMsg] = useState('');

	// ── Products State ──
	const [products, setProducts] = useState([]);
	const [productsLoading, setProductsLoading] = useState(false);
	const [showProductForm, setShowProductForm] = useState(false);
	const [editingProduct, setEditingProduct] = useState(null);
	const [productForm, setProductForm] = useState({
		name: '',
		idVerbose: '',
		type: 'normal-unranked',
		price: '',
		region: '',
		capsuleType: 'be',
		startBe: '',
		endBe: '',
		description: '',
		isActive: true,
	});

	// ── Coupons State ──
	const [coupons, setCoupons] = useState([]);
	const [couponsLoading, setCouponsLoading] = useState(false);
	const [showCouponForm, setShowCouponForm] = useState(false);
	const [editingCoupon, setEditingCoupon] = useState(null);
	const [couponForm, setCouponForm] = useState({
		code: '',
		availableForProducts: '',
		lastsUntil: '',
		maxUses: 99999,
		discounts: [{ percentage: '', leastQuantity: '', mostQuantity: '' }],
	});

	// ── Stock State ──
	const [accounts, setAccounts] = useState([]);
	const [stockLoading, setStockLoading] = useState(false);
	const [showStockForm, setShowStockForm] = useState(false);
	const [stockForm, setStockForm] = useState({
		summonerName: '',
		password: '',
		region: 'br',
		idVerbose: '',
		blueEssence: '',
		currentLevel: '',
		email: '',
		birthdate: '',
		capsuleType: 'be',
		skinShardIds: '',
	});

	// ── Stock Pagination & Filters ──
	const [stockPage, setStockPage] = useState(1);
	const [stockLimit] = useState(50);
	const [stockPagination, setStockPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
	const [stockStats, setStockStats] = useState({ inStock: 0, sold: 0, mock: 0 });
	const [stockSearch, setStockSearch] = useState('');
	const [stockStatusFilter, setStockStatusFilter] = useState('all');
	const [stockIdVerboseFilter, setStockIdVerboseFilter] = useState('all');
	const [stockMockFilter, setStockMockFilter] = useState('all');

	// ── Edit Account State ──
	const [editingAccount, setEditingAccount] = useState(null);
	const [editAccountForm, setEditAccountForm] = useState({});
	const [editAccountLoading, setEditAccountLoading] = useState(false);

	// ── Bulk Import State ──
	const [bulkImportText, setBulkImportText] = useState('');
	const [bulkImportLoading, setBulkImportLoading] = useState(false);
	const [showBulkImport, setShowBulkImport] = useState(false);

	// ── Mock Stock State ──
	const [mockProductId, setMockProductId] = useState('');
	const [mockQuantity, setMockQuantity] = useState(3);
	const [mockLoading, setMockLoading] = useState(false);
	const [showMockStock, setShowMockStock] = useState(false);

	// ── Variant Management State ──
	const [variantsByProduct, setVariantsByProduct] = useState({});

	// ── Skin Shard Pricing State ──
	const [skinShardPrices, setSkinShardPrices] = useState([]);
	const [skinShardPricingLoading, setSkinShardPricingLoading] = useState(false);

	const authHeaders = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };

	const isVariantType = (type) => VARIANT_PRODUCT_TYPES.includes(type);

	// ── Pricing Tab State ──
	const [pricingProducts, setPricingProducts] = useState([]);
	const [pricingLoading, setPricingLoading] = useState(false);
	const [pricingLocalPrices, setPricingLocalPrices] = useState({});

	const fetchStockCounts = async (t) => {
		try {
			const res = await api.get('/api/v1/stock/counts', { headers: { Authorization: `Bearer ${t || token}` } });
			if (res.data.success) setStockCounts(res.data.data);
		} catch (err) {
			console.error('Failed to fetch stock counts');
		}
	};

	useEffect(() => {
		if (token) {
			fetchProducts(token);
			fetchStockCounts(token);
			fetchCoupons(token);
			fetchAccounts(token, 1);
		}
	}, []);

	// Re-fetch when filters change (except search which needs button click)
	useEffect(() => {
		if (token) {
			fetchAccounts(token, 1);
		}
	}, [stockStatusFilter, stockIdVerboseFilter, stockMockFilter]);

	// ── Auth ──
	const handleLogin = async (e) => {
		e.preventDefault();
		setLoginError('');
		setLoginLoading(true);
		try {
			const res = await api.post('/api/v1/auth/login', { username, password }, { headers: { 'Content-Type': 'application/json' } });
			if (res.data.success) {
				const t = res.data.token;
				setToken(t);
				localStorage.setItem('adminToken', t);
				setIsLoggedIn(true);
				setPassword('');
				fetchProducts(t);
				fetchStockCounts(t);
				fetchCoupons(t);
				fetchAccounts(t);
			}
		} catch (err) {
			setLoginError(err.response?.data?.error || 'Invalid credentials');
		}
		setLoginLoading(false);
	};

	const handleLogout = () => {
		setToken('');
		setIsLoggedIn(false);
		setProducts([]);
		setCoupons([]);
		setAccounts([]);
		localStorage.removeItem('adminToken');
	};

	const handle401 = () => {
		setIsLoggedIn(false);
		setToken('');
		localStorage.removeItem('adminToken');
	};

	const showFlash = (msg) => {
		setSuccessMsg(msg);
		setTimeout(() => setSuccessMsg(''), 3000);
	};

	// ══════════════════════════════════════
	// ── Products CRUD ──
	// ══════════════════════════════════════

	const fetchProducts = async (t) => {
		setProductsLoading(true);
		setError('');
		try {
			const res = await api.get('/api/v1/products/', { headers: { Authorization: `Bearer ${t || token}` } });
			if (res.data.success) {
				setProducts(res.data.data);
				setIsLoggedIn(true);
			}
		} catch (err) {
			if (err.response && err.response.status === 401) handle401();
			else setError('Failed to fetch products');
		}
		setProductsLoading(false);
	};

	const resetProductForm = () => {
		setProductForm({ name: '', idVerbose: '', type: 'normal-unranked', price: '', region: '', capsuleType: 'be', startBe: '', endBe: '', description: '', isActive: true });
		setEditingProduct(null);
		setShowProductForm(false);
	};

	const openCreateProduct = () => {
		resetProductForm();
		setShowProductForm(true);
	};

	const openEditProduct = (p) => {
		setEditingProduct(p);
		setProductForm({
			name: p.name || '',
			idVerbose: p.idVerbose || '',
			type: p.type || 'normal-unranked',
			price: p.price ?? '',
			region: p.region || '',
			capsuleType: p.capsuleType || 'be',
			startBe: p.startBe ?? '',
			endBe: p.endBe ?? '',
			description: Array.isArray(p.description) ? p.description.join('\n') : '',
			isActive: p.isActive !== undefined ? p.isActive : true,
		});
		setShowProductForm(true);
	};

	const handleProductFormChange = (e) => {
		const { name, value, type, checked } = e.target;
		setProductForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
	};

	const submitProduct = async (e) => {
		e.preventDefault();
		setError('');
		const payload = {
			...productForm,
			price: Number(productForm.price),
			startBe: Number(productForm.startBe),
			endBe: Number(productForm.endBe),
			description: productForm.description.split('\n').filter((l) => l.trim() !== ''),
		};
		try {
			if (editingProduct) {
				await api.put(`/api/v1/products/${editingProduct._id}`, payload, authHeaders);
				showFlash('Product updated successfully');
			} else {
				const res = await api.post('/api/v1/products/', payload, authHeaders);
				showFlash('Product created successfully');
				// If variant type, auto-create variant pricing entries
				if (isVariantType(payload.type) && res.data.success) {
					const newProductId = res.data.data._id;
					const definitions = VARIANT_DEFINITIONS[payload.type] || [];
					for (const def of definitions) {
						await api.post('/api/v1/product-pricing/', { productId: newProductId, label: def.label, value: def.value, price: 0 }, authHeaders);
					}
				}
			}
			resetProductForm();
			fetchProducts();
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to save product');
		}
	};

	const deleteProduct = async (id, name) => {
		if (!window.confirm(`Delete product "${name}"? This cannot be undone.`)) return;
		setError('');
		try {
			await api.delete(`/api/v1/products/${id}`, authHeaders);
			showFlash('Product deleted');
			fetchProducts();
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to delete product');
		}
	};

	const [seedLoading, setSeedLoading] = useState(false);

	const seedDefaultProducts = async () => {
		setSeedLoading(true);
		setError('');
		try {
			const res = await api.post('/api/v1/products/seed-defaults', {}, authHeaders);
			if (res.data.success) {
				const { created, skipped } = res.data.data;
				showFlash(`Default products: ${created} created, ${skipped} already existed`);
				fetchProducts();
				fetchStockCounts();
			}
		} catch (err) {
			if (err.response && err.response.status === 401) handle401();
			else setError(err.response?.data?.error || 'Failed to seed default products');
		}
		setSeedLoading(false);
	};

	// ══════════════════════════════════════
	// ── Variant CRUD ──
	// ══════════════════════════════════════

	// ══════════════════════════════════════
	// ── Pricing Tab Logic ──
	// ══════════════════════════════════════

	const fetchPricing = async (t) => {
		setPricingLoading(true);
		try {
			const prodRes = await api.get('/api/v1/products/', { headers: { Authorization: `Bearer ${t || token}` } });
			if (prodRes.data.success) {
				const variantProducts = prodRes.data.data.filter((p) => isVariantType(p.type));
				setPricingProducts(variantProducts);
				// Fetch variants for each product
				const allVariants = {};
				const localPrices = {};
				for (const p of variantProducts) {
					try {
						const vRes = await api.get(`/api/v1/product-pricing/by-product/${p._id}`, { headers: { Authorization: `Bearer ${t || token}` } });
						if (vRes.data.success) {
							allVariants[p._id] = vRes.data.data;
							vRes.data.data.forEach((v) => {
								localPrices[v._id] = v.price;
							});
						}
					} catch (err) {
						console.error('Failed to fetch variants for', p._id);
					}
				}
				setVariantsByProduct((prev) => ({ ...prev, ...allVariants }));
				setPricingLocalPrices(localPrices);
			}
		} catch (err) {
			if (err.response && err.response.status === 401) handle401();
		}
		setPricingLoading(false);
	};

	// ── Skin Shard Pricing Functions ──
	const fetchSkinShardPricing = async () => {
		setSkinShardPricingLoading(true);
		try {
			const res = await api.get('/api/v1/skin-shard-pricing/');
			if (res.data.success) {
				setSkinShardPrices(res.data.data);
			}
		} catch (err) {
			setError('Failed to fetch skin shard pricing');
		}
		setSkinShardPricingLoading(false);
	};

	const updateSkinShardPrice = async (rarity, price) => {
		try {
			await api.put(`/api/v1/skin-shard-pricing/${rarity}`, { price: Number(price) || 0 }, authHeaders);
			showFlash(`${rarity} price updated to ${price}`);
			fetchSkinShardPricing();
		} catch (err) {
			setError('Failed to update skin shard price');
		}
	};

	const handlePricingPriceChange = (variantId, value) => {
		setPricingLocalPrices((prev) => ({ ...prev, [variantId]: value }));
	};

	const savePricingPrice = async (variant, productId) => {
		const newPrice = Number(pricingLocalPrices[variant._id]) || 0;
		try {
			await api.put(`/api/v1/product-pricing/${variant._id}`, { price: newPrice }, authHeaders);
			showFlash(`Price updated: ${variant.label} → $${newPrice}`);
			// Update local state
			setVariantsByProduct((prev) => ({
				...prev,
				[productId]: (prev[productId] || []).map((v) => (v._id === variant._id ? { ...v, price: newPrice } : v)),
			}));
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to update price');
		}
	};

	const saveAllPricingForProduct = async (productId) => {
		const variants = variantsByProduct[productId] || [];
		for (const v of variants) {
			const newPrice = Number(pricingLocalPrices[v._id]) || 0;
			if (newPrice !== v.price) {
				await savePricingPrice(v, productId);
			}
		}
		showFlash('All prices saved');
	};

	// ══════════════════════════════════════
	// ── Coupons CRUD ──
	// ══════════════════════════════════════

	const fetchCoupons = async (t) => {
		setCouponsLoading(true);
		try {
			const res = await api.get('/api/v1/coupons/', { headers: { Authorization: `Bearer ${t || token}` } });
			if (res.data.success) {
				setCoupons(res.data.data);
				setIsLoggedIn(true);
			}
		} catch (err) {
			if (err.response && err.response.status === 401) handle401();
		}
		setCouponsLoading(false);
	};

	const resetCouponForm = () => {
		setCouponForm({ code: '', availableForProducts: '', lastsUntil: '', maxUses: 99999, discounts: [{ percentage: '', leastQuantity: '', mostQuantity: '' }] });
		setEditingCoupon(null);
		setShowCouponForm(false);
	};

	const openCreateCoupon = () => {
		resetCouponForm();
		setShowCouponForm(true);
	};

	const openEditCoupon = (c) => {
		setEditingCoupon(c);
		const expiryDate = c.lastsUntil ? new Date(c.lastsUntil).toISOString().slice(0, 16) : '';
		setCouponForm({
			code: c.code || '',
			availableForProducts: Array.isArray(c.availableForProducts) ? c.availableForProducts.join(', ') : '',
			lastsUntil: expiryDate,
			maxUses: c.maxUses !== undefined ? c.maxUses : 99999,
			discounts: c.discounts && c.discounts.length > 0 ? c.discounts.map((d) => ({ percentage: d.percentage || '', leastQuantity: d.leastQuantity || '', mostQuantity: d.mostQuantity || '' })) : [{ percentage: '', leastQuantity: '', mostQuantity: '' }],
		});
		setShowCouponForm(true);
	};

	const handleCouponFormChange = (e) => {
		const { name, value } = e.target;
		setCouponForm((prev) => ({ ...prev, [name]: value }));
	};

	const handleDiscountChange = (index, field, value) => {
		setCouponForm((prev) => {
			const discounts = [...prev.discounts];
			discounts[index] = { ...discounts[index], [field]: value };
			return { ...prev, discounts };
		});
	};

	const addDiscountTier = () => {
		setCouponForm((prev) => ({ ...prev, discounts: [...prev.discounts, { percentage: '', leastQuantity: '', mostQuantity: '' }] }));
	};

	const removeDiscountTier = (index) => {
		setCouponForm((prev) => ({ ...prev, discounts: prev.discounts.filter((_, i) => i !== index) }));
	};

	const submitCoupon = async (e) => {
		e.preventDefault();
		setError('');
		const payload = {
			code: couponForm.code,
			availableForProducts: couponForm.availableForProducts
				.split(',')
				.map((s) => s.trim())
				.filter((s) => s !== ''),
			lastsUntil: couponForm.lastsUntil ? new Date(couponForm.lastsUntil).getTime() : undefined,
			maxUses: Number(couponForm.maxUses),
			discounts: couponForm.discounts.map((d) => ({
				percentage: Number(d.percentage),
				leastQuantity: Number(d.leastQuantity),
				mostQuantity: Number(d.mostQuantity),
			})),
		};
		try {
			if (editingCoupon) {
				await api.put(`/api/v1/coupons/${editingCoupon._id}`, payload, authHeaders);
				showFlash('Coupon updated successfully');
			} else {
				await api.post('/api/v1/coupons/', payload, authHeaders);
				showFlash('Coupon created successfully');
			}
			resetCouponForm();
			fetchCoupons();
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to save coupon');
		}
	};

	const deleteCoupon = async (id, code) => {
		if (!window.confirm(`Delete coupon "${code}"? This cannot be undone.`)) return;
		setError('');
		try {
			await api.delete(`/api/v1/coupons/${id}`, authHeaders);
			showFlash('Coupon deleted');
			fetchCoupons();
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to delete coupon');
		}
	};

	// ══════════════════════════════════════
	// ── Stock (Accounts) ──
	// ══════════════════════════════════════

	const fetchAccounts = async (t, page = stockPage) => {
		setStockLoading(true);
		try {
			const params = new URLSearchParams();
			params.append('page', page);
			params.append('limit', stockLimit);
			if (stockSearch) params.append('search', stockSearch);
			if (stockStatusFilter !== 'all') params.append('status', stockStatusFilter);
			if (stockIdVerboseFilter !== 'all') params.append('idVerbose', stockIdVerboseFilter);
			if (stockMockFilter !== 'all') params.append('isMock', stockMockFilter);

			const res = await api.get(`/api/v1/accounts/?${params.toString()}`, { headers: { Authorization: `Bearer ${t || token}` } });
			if (res.data.success) {
				setAccounts(res.data.data);
				setStockPagination(res.data.pagination);
				setStockStats(res.data.stats);
				setIsLoggedIn(true);
			}
		} catch (err) {
			if (err.response && err.response.status === 401) handle401();
		}
		setStockLoading(false);
	};

	const handleStockSearch = () => {
		setStockPage(1);
		fetchAccounts(token, 1);
	};

	const handleStockPageChange = (newPage) => {
		setStockPage(newPage);
		fetchAccounts(token, newPage);
	};

	const deleteAccount = async (id, summonerName) => {
		if (!window.confirm(`Delete account "${summonerName}"? This cannot be undone.`)) return;
		try {
			await api.delete(`/api/v1/accounts/${id}`, authHeaders);
			showFlash('Account deleted');
			fetchAccounts(token, stockPage);
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to delete account');
		}
	};

	const openEditAccount = (account) => {
		setEditingAccount(account);
		setEditAccountForm({
			summonerName: account.summonerName || '',
			password: '',
			region: account.region || 'br',
			idVerbose: account.idVerbose || '',
			blueEssence: account.blueEssence ?? 0,
			currentLevel: account.currentLevel ?? 0,
			email: account.email || '',
			birthdate: account.birthdate || '',
			capsuleType: account.capsuleType || 'be',
			skinCount: account.skinCount ?? (account.skins ? account.skins.length : 0),
			championCount: account.championCount ?? (account.champions ? account.champions.length : 0),
			rank: account.rank || '',
			stockStatus: account.stockStatus || 'in-stock',
			skinShardIds: account.skinShardIds && account.skinShardIds.length > 0 ? account.skinShardIds.join(', ') : '',
		});
	};

	const handleEditAccountChange = (e) => {
		const { name, value } = e.target;
		setEditAccountForm((prev) => ({ ...prev, [name]: value }));
	};

	const submitEditAccount = async (e) => {
		e.preventDefault();
		if (!editingAccount) return;
		setEditAccountLoading(true);
		setError('');
		const payload = {
			summonerName: editAccountForm.summonerName,
			region: editAccountForm.region,
			idVerbose: editAccountForm.idVerbose,
			blueEssence: Number(editAccountForm.blueEssence) || 0,
			currentLevel: Number(editAccountForm.currentLevel) || 0,
			email: editAccountForm.email,
			birthdate: editAccountForm.birthdate,
			capsuleType: editAccountForm.capsuleType,
			skinCount: Number(editAccountForm.skinCount) || 0,
			championCount: Number(editAccountForm.championCount) || 0,
			rank: editAccountForm.rank,
			stockStatus: editAccountForm.stockStatus,
			skinShardIds: editAccountForm.skinShardIds ? editAccountForm.skinShardIds.split(',').map((s) => Number(s.trim())).filter((n) => !isNaN(n) && n > 0) : [],
		};
		if (editAccountForm.password && editAccountForm.password.trim()) {
			payload.password = editAccountForm.password.trim();
		}
		try {
			await api.put(`/api/v1/accounts/${editingAccount._id}`, payload, authHeaders);
			showFlash('Account updated successfully');
			setEditingAccount(null);
			fetchAccounts(token, stockPage);
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to update account');
		}
		setEditAccountLoading(false);
	};

	const resetStockForm = () => {
		setStockForm({ summonerName: '', password: '', region: 'br', idVerbose: '', blueEssence: '', currentLevel: '', email: '', birthdate: '', capsuleType: 'be', skinShardIds: '' });
		setShowStockForm(false);
	};

	const openAddStock = () => {
		resetStockForm();
		setShowStockForm(true);
	};

	const handleStockFormChange = (e) => {
		const { name, value } = e.target;
		setStockForm((prev) => ({ ...prev, [name]: value }));
	};

	const submitStock = async (e) => {
		e.preventDefault();
		setError('');
		const skinShardIds = stockForm.skinShardIds ? stockForm.skinShardIds.split(',').map((s) => Number(s.trim())).filter((n) => !isNaN(n) && n > 0) : [];
		const payload = {
			...stockForm,
			blueEssence: Number(stockForm.blueEssence) || 0,
			currentLevel: Number(stockForm.currentLevel) || 0,
			skinShardIds,
		};
		try {
			await api.post('/api/v1/accounts/stock', payload, authHeaders);
			showFlash('Account added to stock');
			resetStockForm();
			fetchAccounts();
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to add stock');
		}
	};

	// ── Bulk Import ──
	const submitBulkImport = async () => {
		if (!bulkImportText.trim()) return;
		setBulkImportLoading(true);
		setError('');
		try {
			const res = await api.post('/api/v1/accounts/bulk-stock', { accounts: bulkImportText }, authHeaders);
			if (res.data.success) {
				showFlash(`${res.data.data.count} accounts imported successfully`);
				setBulkImportText('');
				setShowBulkImport(false);
				fetchAccounts();
			}
		} catch (err) {
			setError(err.response?.data?.error || 'Bulk import failed');
		}
		setBulkImportLoading(false);
	};

	// ── Mock Stock ──
	const submitMockStock = async () => {
		if (!mockProductId) return;
		setMockLoading(true);
		setError('');
		try {
			const res = await api.post('/api/v1/accounts/mock-stock', { productId: mockProductId, quantity: mockQuantity }, authHeaders);
			if (res.data.success) {
				showFlash(`${res.data.data.count} mock accounts created`);
				fetchAccounts();
			}
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to create mock stock');
		}
		setMockLoading(false);
	};

	const removeMock = async () => {
		if (!window.confirm('Remove ALL mock stock? This cannot be undone.')) return;
		setMockLoading(true);
		setError('');
		try {
			const res = await api.delete('/api/v1/accounts/mock-stock', authHeaders);
			if (res.data.success) {
				showFlash(`${res.data.data.deleted} mock accounts removed`);
				fetchAccounts();
			}
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to remove mock stock');
		}
		setMockLoading(false);
	};

	// Get unique idVerboses from products for filter dropdown
	const uniqueIdVerboses = [...new Set(products.map((p) => p.idVerbose).filter(Boolean))].sort();

	// Stock counts per product
	const [stockCounts, setStockCounts] = useState({});

	// ── Styles ──
	const styles = {
		page: { minHeight: '100vh', background: '#0f1117', color: '#e0e0e0', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", padding: '40px 20px' },
		container: { maxWidth: 1100, margin: '0 auto' },
		heading: { fontSize: 28, fontWeight: 700, marginBottom: 20, color: '#fff' },
		card: { background: '#1a1d27', borderRadius: 8, padding: 24, marginBottom: 20, border: '1px solid #2a2d3a' },
		input: { width: '100%', padding: '10px 14px', background: '#0f1117', border: '1px solid #2a2d3a', borderRadius: 6, color: '#e0e0e0', fontSize: 14, marginBottom: 12, boxSizing: 'border-box' },
		select: { width: '100%', padding: '10px 14px', background: '#0f1117', border: '1px solid #2a2d3a', borderRadius: 6, color: '#e0e0e0', fontSize: 14, marginBottom: 12, boxSizing: 'border-box' },
		textarea: { width: '100%', padding: '10px 14px', background: '#0f1117', border: '1px solid #2a2d3a', borderRadius: 6, color: '#e0e0e0', fontSize: 14, marginBottom: 12, minHeight: 80, resize: 'vertical', boxSizing: 'border-box' },
		btnPrimary: { padding: '10px 22px', background: '#6c5ce7', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
		btnDanger: { padding: '6px 14px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
		btnSecondary: { padding: '6px 14px', background: '#2a2d3a', color: '#e0e0e0', border: '1px solid #3a3d4a', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
		btnEdit: { padding: '6px 14px', background: '#00b894', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, marginRight: 8 },
		table: { width: '100%', borderCollapse: 'collapse' },
		th: { textAlign: 'left', padding: '12px 10px', borderBottom: '2px solid #2a2d3a', color: '#aaa', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 },
		td: { padding: '12px 10px', borderBottom: '1px solid #1e2130', fontSize: 14 },
		badge: (active) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: active ? '#00b89422' : '#e74c3c22', color: active ? '#00b894' : '#e74c3c' }),
		badgeYellow: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#fdcb6e22', color: '#fdcb6e' },
		label: { display: 'block', fontSize: 13, color: '#aaa', marginBottom: 4 },
		row: { display: 'flex', gap: 12 },
		col: { flex: 1 },
		errorBox: { background: '#e74c3c22', color: '#e74c3c', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 14 },
		successBox: { background: '#00b89422', color: '#00b894', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 14 },
		topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
		tab: (active) => ({
			padding: '10px 24px',
			background: active ? '#6c5ce7' : 'transparent',
			color: active ? '#fff' : '#aaa',
			border: active ? 'none' : '1px solid #2a2d3a',
			borderRadius: 6,
			cursor: 'pointer',
			fontSize: 14,
			fontWeight: 600,
			marginRight: 8,
		}),
		tabBar: { display: 'flex', marginBottom: 24 },
		filterBar: { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' },
		statCard: { background: '#1a1d27', border: '1px solid #2a2d3a', borderRadius: 8, padding: '12px 20px', textAlign: 'center', flex: 1 },
		statNumber: { fontSize: 24, fontWeight: 700, color: '#6c5ce7' },
		statLabel: { fontSize: 11, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1 },
	};

	const formatDate = (ts) => {
		if (!ts) return '-';
		const d = new Date(ts);
		return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	};

	// ── Login Screen ──
	if (!isLoggedIn) {
		return (
			<div style={styles.page}>
				<div style={{ ...styles.container, maxWidth: 400 }}>
					<h1 style={{ ...styles.heading, textAlign: 'center' }}>Admin Login</h1>
					<div style={styles.card}>
						{loginError && <div style={styles.errorBox}>{loginError}</div>}
						<form onSubmit={handleLogin}>
							<label style={styles.label}>Username</label>
							<input style={styles.input} type='text' value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus />
							<label style={styles.label}>Password</label>
							<input style={styles.input} type='password' value={password} onChange={(e) => setPassword(e.target.value)} required />
							<button style={{ ...styles.btnPrimary, width: '100%', marginTop: 8 }} type='submit' disabled={loginLoading}>
								{loginLoading ? 'Logging in...' : 'Login'}
							</button>
						</form>
					</div>
				</div>
			</div>
		);
	}

	// ── Admin Dashboard ──
	return (
		<div style={styles.page}>
			<div style={styles.container}>
				<div style={styles.topBar}>
					<h1 style={{ ...styles.heading, marginBottom: 0 }}>Admin Panel</h1>
					<button style={styles.btnSecondary} onClick={handleLogout}>
						Logout
					</button>
				</div>

				{successMsg && <div style={styles.successBox}>{successMsg}</div>}
				{error && <div style={styles.errorBox}>{error}</div>}

				{/* ── Tabs ── */}
				<div style={styles.tabBar}>
					<button style={styles.tab(activeTab === 'products')} onClick={() => setActiveTab('products')}>
						Products
					</button>
					<button style={styles.tab(activeTab === 'pricing')} onClick={() => { setActiveTab('pricing'); fetchPricing(); }}>
						Pricing
					</button>
					<button style={styles.tab(activeTab === 'coupons')} onClick={() => setActiveTab('coupons')}>
						Coupons
					</button>
					<button style={styles.tab(activeTab === 'skinShardPricing')} onClick={() => { setActiveTab('skinShardPricing'); fetchSkinShardPricing(); }}>
						Skin Shards
					</button>
					<button style={styles.tab(activeTab === 'stock')} onClick={() => setActiveTab('stock')}>
						Stock
					</button>
				</div>

				{/* ══════════════════════════════════════ */}
				{/* ── PRODUCTS TAB ── */}
				{/* ══════════════════════════════════════ */}
				{activeTab === 'products' && (
					<>
						<div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
							<button style={styles.btnPrimary} onClick={openCreateProduct}>
								+ New Product
							</button>
							<button style={{ ...styles.btnPrimary, background: '#00b894' }} onClick={seedDefaultProducts} disabled={seedLoading}>
								{seedLoading ? 'Creating...' : 'Create Default Products'}
							</button>
						</div>

						{showProductForm && (
							<div style={styles.card}>
								<h2 style={{ fontSize: 20, marginBottom: 16, color: '#fff' }}>{editingProduct ? 'Edit Product' : 'Create Product'}</h2>
								<form onSubmit={submitProduct}>
									<div style={styles.row}>
										<div style={styles.col}>
											<label style={styles.label}>Name</label>
											<input style={styles.input} name='name' value={productForm.name} onChange={handleProductFormChange} required placeholder='e.g. Campeones NFA' />
										</div>
										<div style={styles.col}>
											<label style={styles.label}>ID Verbose</label>
											<input style={styles.input} name='idVerbose' value={productForm.idVerbose} onChange={handleProductFormChange} required placeholder='e.g. nfa_campeones' />
										</div>
									</div>
									<div style={styles.row}>
										<div style={styles.col}>
											<label style={styles.label}>Type</label>
											<select style={styles.select} name='type' value={productForm.type} onChange={handleProductFormChange}>
												<option value='normal-unranked'>Normal Unranked</option>
												<option value='skin-unranked'>Skin Unranked</option>
												<option value='champion'>Champion</option>
												<option value='skins'>Skins</option>
												<option value='rank'>Rank</option>
												<option value='unranked'>Unranked (BE)</option>
											</select>
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Region</label>
											<input style={styles.input} name='region' value={productForm.region} onChange={handleProductFormChange} required placeholder='e.g. las, br, na, euw' />
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Base Price</label>
											<input style={styles.input} name='price' type='number' value={productForm.price} onChange={handleProductFormChange} required placeholder='0' />
										</div>
									</div>
									{isVariantType(productForm.type) && (
										<div style={{ background: '#0f111722', border: '1px solid #2a2d3a', borderRadius: 6, padding: 12, marginBottom: 12 }}>
											<span style={{ fontSize: 12, color: '#6c5ce7' }}>
												This product type has variants: {(VARIANT_DEFINITIONS[productForm.type] || []).map((d) => d.label).join(', ')}
											</span>
											{!editingProduct && <span style={{ fontSize: 12, color: '#aaa', display: 'block', marginTop: 4 }}>Variant pricing entries will be created automatically. Set prices after creating the product.</span>}
										</div>
									)}
									<div style={styles.row}>
										<div style={styles.col}>
											<label style={styles.label}>Capsule Type</label>
											<select style={styles.select} name='capsuleType' value={productForm.capsuleType} onChange={handleProductFormChange}>
												<option value='be'>Blue Essence</option>
												<option value='capsule'>Capsule</option>
											</select>
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Start BE</label>
											<input style={styles.input} name='startBe' type='number' value={productForm.startBe} onChange={handleProductFormChange} required placeholder='e.g. 50000' />
										</div>
										<div style={styles.col}>
											<label style={styles.label}>End BE</label>
											<input style={styles.input} name='endBe' type='number' value={productForm.endBe} onChange={handleProductFormChange} required placeholder='e.g. 60000' />
										</div>
									</div>
									<label style={styles.label}>Description (one item per line)</label>
									<textarea style={styles.textarea} name='description' value={productForm.description} onChange={handleProductFormChange} placeholder={'At least level 30\nVirgin MMR\n0% ban chance'} />
									<label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
										<input type='checkbox' name='isActive' checked={productForm.isActive} onChange={handleProductFormChange} />
										Active
									</label>
									<div style={{ display: 'flex', gap: 10 }}>
										<button style={styles.btnPrimary} type='submit'>
											{editingProduct ? 'Update Product' : 'Create Product'}
										</button>
										<button style={styles.btnSecondary} type='button' onClick={resetProductForm}>
											Cancel
										</button>
									</div>
								</form>
							</div>
						)}

						<div style={styles.card}>
							{productsLoading ? (
								<p style={{ textAlign: 'center', color: '#aaa' }}>Loading products...</p>
							) : products.length === 0 ? (
								<p style={{ textAlign: 'center', color: '#aaa' }}>No active products found.</p>
							) : (
								<div style={{ overflowX: 'auto' }}>
									<table style={styles.table}>
										<thead>
											<tr>
												<th style={styles.th}>Name</th>
												<th style={styles.th}>ID Verbose</th>
												<th style={styles.th}>Type</th>
												<th style={styles.th}>Region</th>
												<th style={styles.th}>Price</th>
												<th style={styles.th}>BE Range</th>
												<th style={styles.th}>Stock</th>
												<th style={styles.th}>Status</th>
												<th style={styles.th}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{products.map((p) => (
												<tr key={p._id}>
													<td style={styles.td}>{p.name}</td>
													<td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 13 }}>{p.idVerbose}</td>
													<td style={styles.td}>
														{isVariantType(p.type) ? (
															<span style={styles.badgeYellow}>{p.type}</span>
														) : (
															p.type
														)}
													</td>
													<td style={{ ...styles.td, textTransform: 'uppercase' }}>{p.region}</td>
													<td style={styles.td}>${p.price}</td>
													<td style={styles.td}>
														{p.startBe?.toLocaleString()} - {p.endBe?.toLocaleString()}
													</td>
													<td style={styles.td}>
														<span style={{ color: (stockCounts[p._id]?.total || 0) > 0 ? '#00b894' : '#e74c3c', fontWeight: 600 }}>{stockCounts[p._id]?.total || 0}</span>
													</td>
													<td style={styles.td}>
														<span style={styles.badge(p.isActive)}>{p.isActive ? 'Active' : 'Inactive'}</span>
													</td>
													<td style={styles.td}>
														<button style={styles.btnEdit} onClick={() => openEditProduct(p)}>
															Edit
														</button>
														<button style={styles.btnDanger} onClick={() => deleteProduct(p._id, p.name)}>
															Delete
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</>
				)}

				{/* ══════════════════════════════════════ */}
				{/* ── PRICING TAB ── */}
				{/* ══════════════════════════════════════ */}
				{activeTab === 'pricing' && (
					<>
						{pricingLoading ? (
							<p style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>Loading pricing data...</p>
						) : pricingProducts.length === 0 ? (
							<div style={styles.card}>
								<p style={{ textAlign: 'center', color: '#aaa' }}>No variant products found. Create a product with type Champion, Skins, Rank, or Unranked first.</p>
							</div>
						) : (
							pricingProducts.map((p) => {
								const variants = variantsByProduct[p._id] || [];
								const definitions = VARIANT_DEFINITIONS[p.type] || [];
								return (
									<div key={p._id} style={styles.card}>
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
											<div>
												<h2 style={{ fontSize: 20, color: '#fff', margin: 0 }}>{p.name}</h2>
												<div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>
													<span style={{ fontFamily: 'monospace', color: '#6c5ce7' }}>{p.idVerbose}</span>
													<span style={{ margin: '0 8px' }}>|</span>
													<span style={styles.badgeYellow}>{p.type}</span>
													<span style={{ margin: '0 8px' }}>|</span>
													<span style={{ textTransform: 'uppercase' }}>{p.region}</span>
												</div>
											</div>
											<button style={styles.btnPrimary} onClick={() => saveAllPricingForProduct(p._id)}>
												Save All Prices
											</button>
										</div>
										<table style={styles.table}>
											<thead>
												<tr>
													<th style={styles.th}>Variant</th>
													<th style={styles.th}>Value</th>
													<th style={styles.th}>Current Price</th>
													<th style={styles.th}>New Price</th>
													<th style={styles.th}>Action</th>
												</tr>
											</thead>
											<tbody>
												{definitions.map((def) => {
													const variant = variants.find((v) => v.value === def.value);
													if (!variant) {
														return (
															<tr key={def.value}>
																<td style={styles.td}>{def.label}</td>
																<td style={{ ...styles.td, fontFamily: 'monospace' }}>{def.value}</td>
																<td style={{ ...styles.td, color: '#e74c3c' }}>Not created</td>
																<td style={styles.td}>-</td>
																<td style={styles.td}>
																	<button
																		style={styles.btnEdit}
																		onClick={async () => {
																			try {
																				await api.post('/api/v1/product-pricing/', { productId: p._id, label: def.label, value: def.value, price: 0 }, authHeaders);
																				showFlash(`Created variant: ${def.label}`);
																				fetchPricing();
																			} catch (err) {
																				setError('Failed to create variant');
																			}
																		}}
																	>
																		Create
																	</button>
																</td>
															</tr>
														);
													}
													return (
														<tr key={def.value}>
															<td style={styles.td}>{def.label}</td>
															<td style={{ ...styles.td, fontFamily: 'monospace' }}>{def.value}</td>
															<td style={styles.td}>
																<span style={{ color: variant.price > 0 ? '#00b894' : '#e74c3c', fontWeight: 600 }}>${variant.price}</span>
															</td>
															<td style={styles.td}>
																<input
																	style={{ ...styles.input, width: 120, marginBottom: 0 }}
																	type='number'
																	value={pricingLocalPrices[variant._id] !== undefined ? pricingLocalPrices[variant._id] : ''}
																	onChange={(e) => handlePricingPriceChange(variant._id, e.target.value)}
																	placeholder='0'
																/>
															</td>
															<td style={styles.td}>
																<button style={styles.btnEdit} onClick={() => savePricingPrice(variant, p._id)}>
																	Save
																</button>
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								);
							})
						)}
					</>
				)}

				{/* ══════════════════════════════════════ */}
				{/* ── COUPONS TAB ── */}
				{/* ══════════════════════════════════════ */}
				{activeTab === 'coupons' && (
					<>
						<div style={{ marginBottom: 16 }}>
							<button style={styles.btnPrimary} onClick={openCreateCoupon}>
								+ New Coupon
							</button>
						</div>

						{showCouponForm && (
							<div style={styles.card}>
								<h2 style={{ fontSize: 20, marginBottom: 16, color: '#fff' }}>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</h2>
								<form onSubmit={submitCoupon}>
									<div style={styles.row}>
										<div style={styles.col}>
											<label style={styles.label}>Code</label>
											<input style={styles.input} name='code' value={couponForm.code} onChange={handleCouponFormChange} required placeholder='e.g. SUMMER20' />
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Max Uses</label>
											<input style={styles.input} name='maxUses' type='number' value={couponForm.maxUses} onChange={handleCouponFormChange} placeholder='99999' />
										</div>
									</div>
									<div style={styles.row}>
										<div style={styles.col}>
											<label style={styles.label}>Available For Products (comma-separated idVerbose)</label>
											<input style={styles.input} name='availableForProducts' value={couponForm.availableForProducts} onChange={handleCouponFormChange} placeholder='e.g. br_50, br_cap' />
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Expires At</label>
											<input style={styles.input} name='lastsUntil' type='datetime-local' value={couponForm.lastsUntil} onChange={handleCouponFormChange} />
										</div>
									</div>
									<div style={{ marginBottom: 12 }}>
										<label style={{ ...styles.label, marginBottom: 8 }}>Discount Tiers</label>
										{couponForm.discounts.map((d, i) => (
											<div key={i} style={{ ...styles.row, alignItems: 'flex-end', marginBottom: 8 }}>
												<div style={styles.col}>
													{i === 0 && <label style={{ ...styles.label, fontSize: 11 }}>% Off</label>}
													<input style={{ ...styles.input, marginBottom: 0 }} type='number' value={d.percentage} onChange={(e) => handleDiscountChange(i, 'percentage', e.target.value)} placeholder='e.g. 10' required />
												</div>
												<div style={styles.col}>
													{i === 0 && <label style={{ ...styles.label, fontSize: 11 }}>Min Qty</label>}
													<input style={{ ...styles.input, marginBottom: 0 }} type='number' value={d.leastQuantity} onChange={(e) => handleDiscountChange(i, 'leastQuantity', e.target.value)} placeholder='e.g. 1' required />
												</div>
												<div style={styles.col}>
													{i === 0 && <label style={{ ...styles.label, fontSize: 11 }}>Max Qty</label>}
													<input style={{ ...styles.input, marginBottom: 0 }} type='number' value={d.mostQuantity} onChange={(e) => handleDiscountChange(i, 'mostQuantity', e.target.value)} placeholder='e.g. 5' required />
												</div>
												<div>
													{couponForm.discounts.length > 1 && (
														<button type='button' style={{ ...styles.btnDanger, padding: '8px 12px' }} onClick={() => removeDiscountTier(i)}>
															X
														</button>
													)}
												</div>
											</div>
										))}
										<button type='button' style={{ ...styles.btnSecondary, marginTop: 4 }} onClick={addDiscountTier}>
											+ Add Tier
										</button>
									</div>
									<div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
										<button style={styles.btnPrimary} type='submit'>
											{editingCoupon ? 'Update Coupon' : 'Create Coupon'}
										</button>
										<button style={styles.btnSecondary} type='button' onClick={resetCouponForm}>
											Cancel
										</button>
									</div>
								</form>
							</div>
						)}

						<div style={styles.card}>
							{couponsLoading ? (
								<p style={{ textAlign: 'center', color: '#aaa' }}>Loading coupons...</p>
							) : coupons.length === 0 ? (
								<p style={{ textAlign: 'center', color: '#aaa' }}>No coupons found.</p>
							) : (
								<div style={{ overflowX: 'auto' }}>
									<table style={styles.table}>
										<thead>
											<tr>
												<th style={styles.th}>Code</th>
												<th style={styles.th}>Uses</th>
												<th style={styles.th}>Max Uses</th>
												<th style={styles.th}>Expires</th>
												<th style={styles.th}>Discounts</th>
												<th style={styles.th}>Products</th>
												<th style={styles.th}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{coupons.map((c) => {
												const isExpired = c.lastsUntil && c.lastsUntil < Date.now();
												const isMaxed = c.uses >= c.maxUses;
												return (
													<tr key={c._id}>
														<td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 600 }}>{c.code}</td>
														<td style={styles.td}>
															<span style={styles.badge(!isMaxed)}>
																{c.uses || 0} / {c.maxUses}
															</span>
														</td>
														<td style={styles.td}>{c.maxUses}</td>
														<td style={styles.td}>
															{c.lastsUntil ? (
																<span style={{ color: isExpired ? '#e74c3c' : '#e0e0e0' }}>
																	{formatDate(c.lastsUntil)}
																	{isExpired && ' (expired)'}
																</span>
															) : (
																<span style={{ color: '#555' }}>No expiry</span>
															)}
														</td>
														<td style={styles.td}>
															{c.discounts && c.discounts.length > 0 ? (
																<div>
																	{c.discounts.map((d, i) => (
																		<div key={i} style={{ fontSize: 12, marginBottom: 2 }}>
																			{d.percentage}% off (qty {d.leastQuantity}-{d.mostQuantity})
																		</div>
																	))}
																</div>
															) : (
																'-'
															)}
														</td>
														<td style={{ ...styles.td, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
															{c.availableForProducts && c.availableForProducts.length > 0 ? c.availableForProducts.join(', ') : 'All'}
														</td>
														<td style={styles.td}>
															<button style={styles.btnEdit} onClick={() => openEditCoupon(c)}>
																Edit
															</button>
															<button style={styles.btnDanger} onClick={() => deleteCoupon(c._id, c.code)}>
																Delete
															</button>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</>
				)}

				{/* ══════════════════════════════════════ */}
				{/* ── SKIN SHARD PRICING TAB ── */}
				{/* ══════════════════════════════════════ */}
				{activeTab === 'skinShardPricing' && (
					<>
						<div style={styles.card}>
							<h2 style={{ fontSize: 20, marginBottom: 8, color: '#fff' }}>Skin Shard Extra Pricing</h2>
							<p style={{ fontSize: 12, color: '#aaa', marginBottom: 16 }}>
								Set the extra price added to the product when a customer selects a skin shard of each rarity.
								This is added on top of the product/variant base price.
							</p>
							{skinShardPricingLoading ? (
								<p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p>
							) : (
								<table style={styles.table}>
									<thead>
										<tr>
											<th style={styles.th}>Rarity</th>
											<th style={styles.th}>Extra Price (R$)</th>
											<th style={styles.th}>Actions</th>
										</tr>
									</thead>
									<tbody>
										{['DEFAULT', 'RARE', 'EPIC', 'LEGENDARY', 'ULTIMATE', 'MYTHIC'].map((rarity) => {
											const existing = skinShardPrices.find((p) => p.rarity === rarity);
											const rarityColors = { DEFAULT: '#8e8e8e', RARE: '#1e90ff', EPIC: '#b146c2', LEGENDARY: '#e8872a', ULTIMATE: '#e44040', MYTHIC: '#ee5a9c' };
											return (
												<tr key={rarity}>
													<td style={styles.td}>
														<span style={{ fontWeight: 700, color: rarityColors[rarity] || '#fff' }}>{rarity}</span>
													</td>
													<td style={styles.td}>
														<input
															id={`ssp-${rarity}`}
															style={{ ...styles.input, width: 100, marginBottom: 0 }}
															type='number'
															step='0.01'
															min='0'
															defaultValue={existing ? existing.price : 0}
														/>
													</td>
													<td style={styles.td}>
														<button
															style={styles.btnPrimary}
															onClick={() => {
																const val = document.getElementById(`ssp-${rarity}`).value;
																updateSkinShardPrice(rarity, val);
															}}
														>
															Save
														</button>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							)}
						</div>
					</>
				)}

				{/* ══════════════════════════════════════ */}
				{/* ── STOCK TAB ── */}
				{/* ══════════════════════════════════════ */}
				{activeTab === 'stock' && (
					<>
						{/* Stock Summary */}
						<div style={{ ...styles.row, marginBottom: 20 }}>
							<div style={styles.statCard}>
								<div style={styles.statNumber}>{stockStats.inStock - stockStats.mock}</div>
								<div style={styles.statLabel}>In Stock (Real)</div>
							</div>
							<div style={styles.statCard}>
								<div style={{ ...styles.statNumber, color: '#fdcb6e' }}>{stockStats.mock}</div>
								<div style={styles.statLabel}>Mock</div>
							</div>
							<div style={styles.statCard}>
								<div style={{ ...styles.statNumber, color: '#00b894' }}>{stockStats.sold}</div>
								<div style={styles.statLabel}>Sold</div>
							</div>
							<div style={styles.statCard}>
								<div style={{ ...styles.statNumber, color: '#aaa' }}>{stockStats.inStock + stockStats.sold}</div>
								<div style={styles.statLabel}>Total</div>
							</div>
						</div>

						<div style={{ marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
							<button style={styles.btnPrimary} onClick={openAddStock}>
								+ Add Stock
							</button>
							<button style={{ ...styles.btnPrimary, background: '#e17055' }} onClick={() => setShowBulkImport(!showBulkImport)}>
								Bulk Import
							</button>
							<button style={{ ...styles.btnPrimary, background: '#fdcb6e', color: '#000' }} onClick={() => setShowMockStock(!showMockStock)}>
								{showMockStock ? 'Hide Mock Tools' : 'Mock Stock Tools'}
							</button>
							<button style={styles.btnSecondary} onClick={() => fetchAccounts()}>
								Refresh
							</button>
						</div>

						{/* Bulk Import */}
						{showBulkImport && (
							<div style={styles.card}>
								<h2 style={{ fontSize: 20, marginBottom: 8, color: '#fff' }}>Bulk Import Accounts</h2>
								<p style={{ fontSize: 12, color: '#aaa', marginBottom: 12 }}>
									One account per line. Format: <code style={{ color: '#fdcb6e' }}>summonerName:password:region:blueEssence:level:email:birthdate:createdAt:capsuleType:skinCount:championCount:rank:idVerbose:skinShardIds</code>
									<br />
									Use <code style={{ color: '#fdcb6e' }}>-</code> for empty fields. skinCount and championCount are numbers. skinShardIds is optional (14th field), comma-separated IDs e.g. <code style={{ color: '#fdcb6e' }}>15,56,32</code>.
								</p>
								<textarea
									style={{ ...styles.textarea, minHeight: 150, fontFamily: 'monospace', fontSize: 12 }}
									value={bulkImportText}
									onChange={(e) => setBulkImportText(e.target.value)}
									placeholder='Player1:pass123:br:50000:30:email@test.com:2000-01-01:2023-01-01:be:15:45:-:nfa_campeones:15,56,32'
								/>
								<div style={{ display: 'flex', gap: 10 }}>
									<button style={styles.btnPrimary} onClick={submitBulkImport} disabled={bulkImportLoading}>
										{bulkImportLoading ? 'Importing...' : 'Import'}
									</button>
									<button style={styles.btnSecondary} onClick={() => { setShowBulkImport(false); setBulkImportText(''); }}>
										Cancel
									</button>
								</div>
							</div>
						)}

						{/* Mock Stock */}
						{showMockStock && (
							<div style={styles.card}>
								<h2 style={{ fontSize: 20, marginBottom: 8, color: '#fff' }}>Mock Stock Tools</h2>
								<p style={{ fontSize: 12, color: '#aaa', marginBottom: 12 }}>
									Generate fake accounts for testing. Mock accounts have <code style={{ color: '#fdcb6e' }}>isMock: true</code> and won't be delivered to real customers.
									For variant products, mock accounts are created for every variant.
								</p>
								<div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
									<div>
										<label style={styles.label}>Product</label>
										<select style={styles.input} value={mockProductId} onChange={(e) => setMockProductId(e.target.value)}>
											<option value="">-- Select product --</option>
											{products.map((p) => (
												<option key={p._id} value={p._id}>{p.name} ({p.idVerbose}) - {p.region.toUpperCase()}</option>
											))}
										</select>
									</div>
									<div>
										<label style={styles.label}>Qty per variant</label>
										<input style={{ ...styles.input, width: 70 }} type="number" min="1" max="50" value={mockQuantity} onChange={(e) => setMockQuantity(Number(e.target.value) || 1)} />
									</div>
									<button style={styles.btnPrimary} onClick={submitMockStock} disabled={mockLoading || !mockProductId}>
										{mockLoading ? 'Creating...' : 'Add Mock Stock'}
									</button>
									<button style={{ ...styles.btnPrimary, background: '#e74c3c' }} onClick={removeMock} disabled={mockLoading}>
										{mockLoading ? 'Removing...' : 'Remove All Mock Stock'}
									</button>
								</div>
							</div>
						)}

						{showStockForm && (
							<div style={styles.card}>
								<h2 style={{ fontSize: 20, marginBottom: 16, color: '#fff' }}>Add Account to Stock</h2>
								<form onSubmit={submitStock}>
									<div style={styles.row}>
										<div style={styles.col}>
											<label style={styles.label}>Summoner Name</label>
											<input style={styles.input} name='summonerName' value={stockForm.summonerName} onChange={handleStockFormChange} required placeholder='e.g. Player123' />
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Password</label>
											<input style={styles.input} name='password' type='text' value={stockForm.password} onChange={handleStockFormChange} required placeholder='Account password' />
										</div>
									</div>
									<div style={styles.row}>
										<div style={styles.col}>
											<label style={styles.label}>ID Verbose (product link)</label>
											<select style={styles.select} name='idVerbose' value={stockForm.idVerbose} onChange={handleStockFormChange} required>
												<option value=''>-- Select product --</option>
												{products.map((p) => (
													<option key={p._id} value={p.idVerbose}>
														{p.idVerbose} - {p.name}
													</option>
												))}
												{uniqueIdVerboses
													.filter((iv) => !products.find((p) => p.idVerbose === iv))
													.map((iv) => (
														<option key={iv} value={iv}>
															{iv}
														</option>
													))}
											</select>
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Region</label>
											<select style={styles.select} name='region' value={stockForm.region} onChange={handleStockFormChange} required>
												{['br', 'las', 'lan', 'na', 'euw', 'eune', 'jp', 'oce', 'kr', 'tr', 'ru'].map((r) => (
													<option key={r} value={r}>
														{r.toUpperCase()}
													</option>
												))}
											</select>
										</div>
									</div>
									<div style={styles.row}>
										<div style={styles.col}>
											<label style={styles.label}>Blue Essence</label>
											<input style={styles.input} name='blueEssence' type='number' value={stockForm.blueEssence} onChange={handleStockFormChange} placeholder='0' />
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Level</label>
											<input style={styles.input} name='currentLevel' type='number' value={stockForm.currentLevel} onChange={handleStockFormChange} placeholder='30' />
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Capsule Type</label>
											<select style={styles.select} name='capsuleType' value={stockForm.capsuleType} onChange={handleStockFormChange}>
												<option value='be'>Blue Essence</option>
												<option value='capsule'>Capsule</option>
											</select>
										</div>
									</div>
									<div style={styles.row}>
										<div style={styles.col}>
											<label style={styles.label}>Email</label>
											<input style={styles.input} name='email' value={stockForm.email} onChange={handleStockFormChange} placeholder='Optional' />
										</div>
										<div style={styles.col}>
											<label style={styles.label}>Birthdate</label>
											<input style={styles.input} name='birthdate' value={stockForm.birthdate} onChange={handleStockFormChange} placeholder='Optional' />
										</div>
									</div>
									<div style={styles.row}>
										<div style={{ ...styles.col, flex: '1 1 100%' }}>
											<label style={styles.label}>Skin Shard IDs (comma-separated)</label>
											<input style={styles.input} name='skinShardIds' value={stockForm.skinShardIds} onChange={handleStockFormChange} placeholder='e.g. 15,56,32' />
										</div>
									</div>
									<div style={{ display: 'flex', gap: 10 }}>
										<button style={styles.btnPrimary} type='submit'>
											Add to Stock
										</button>
										<button style={styles.btnSecondary} type='button' onClick={resetStockForm}>
											Cancel
										</button>
									</div>
								</form>
							</div>
						)}

						{/* Filters */}
						<div style={{ ...styles.card, padding: 16, marginBottom: 16 }}>
							<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
								<div>
									<label style={styles.label}>Search Username</label>
									<input
										style={{ ...styles.input, marginBottom: 0, minWidth: 180 }}
										placeholder="Search..."
										value={stockSearch}
										onChange={(e) => setStockSearch(e.target.value)}
										onKeyPress={(e) => e.key === 'Enter' && handleStockSearch()}
									/>
								</div>
								<div>
									<label style={styles.label}>Status</label>
									<select style={{ ...styles.select, marginBottom: 0, minWidth: 120 }} value={stockStatusFilter} onChange={(e) => { setStockStatusFilter(e.target.value); setStockPage(1); }}>
										<option value='all'>All</option>
										<option value='in-stock'>In Stock</option>
										<option value='sold'>Sold</option>
										<option value='banned'>Banned</option>
									</select>
								</div>
								<div>
									<label style={styles.label}>Product</label>
									<select style={{ ...styles.select, marginBottom: 0, minWidth: 150 }} value={stockIdVerboseFilter} onChange={(e) => { setStockIdVerboseFilter(e.target.value); setStockPage(1); }}>
										<option value='all'>All Products</option>
										{uniqueIdVerboses.map((iv) => (
											<option key={iv} value={iv}>{iv}</option>
										))}
									</select>
								</div>
								<div>
									<label style={styles.label}>Type</label>
									<select style={{ ...styles.select, marginBottom: 0, minWidth: 100 }} value={stockMockFilter} onChange={(e) => { setStockMockFilter(e.target.value); setStockPage(1); }}>
										<option value='all'>All</option>
										<option value='false'>Real</option>
										<option value='true'>Mock</option>
									</select>
								</div>
								<button style={styles.btnPrimary} onClick={handleStockSearch}>
									Search
								</button>
							</div>
							<div style={{ color: '#aaa', fontSize: 13, marginTop: 12 }}>
								Showing {accounts.length} of {stockPagination.total} accounts (Page {stockPagination.page} of {stockPagination.totalPages})
							</div>
						</div>

						<div style={styles.card}>
							{stockLoading ? (
								<p style={{ textAlign: 'center', color: '#aaa' }}>Loading stock...</p>
							) : accounts.length === 0 ? (
								<p style={{ textAlign: 'center', color: '#aaa' }}>No accounts found.</p>
							) : (
								<div style={{ overflowX: 'auto' }}>
									<table style={styles.table}>
										<thead>
											<tr>
												<th style={styles.th}>Summoner</th>
												<th style={styles.th}>ID Verbose</th>
												<th style={styles.th}>Status</th>
												<th style={styles.th}>Region</th>
												<th style={styles.th}>BE</th>
												<th style={styles.th}>Level</th>
												<th style={styles.th}>Skins</th>
												<th style={styles.th}>Champs</th>
												<th style={styles.th}>Rank</th>
												<th style={styles.th}>Skin Shards</th>
												<th style={styles.th}>Added</th>
												<th style={styles.th}>Actions</th>
											</tr>
										</thead>
										<tbody>
											{accounts.map((a) => (
												<tr key={a._id} style={a.isMock ? { opacity: 0.6 } : {}}>
													<td style={styles.td}>
														{a.summonerName}
														{a.isMock && <span style={{ marginLeft: 6, fontSize: 10, color: '#fdcb6e', fontWeight: 700 }}>MOCK</span>}
													</td>
													<td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 13 }}>{a.idVerbose || '-'}</td>
													<td style={styles.td}>
														<span style={{
															padding: '3px 8px',
															borderRadius: 4,
															fontSize: 11,
															fontWeight: 600,
															background: a.stockStatus === 'in-stock' ? '#00b89422' : a.stockStatus === 'sold' ? '#6c5ce722' : '#e74c3c22',
															color: a.stockStatus === 'in-stock' ? '#00b894' : a.stockStatus === 'sold' ? '#6c5ce7' : '#e74c3c',
														}}>
															{a.stockStatus}
														</span>
													</td>
													<td style={{ ...styles.td, textTransform: 'uppercase' }}>{a.region}</td>
													<td style={styles.td}>{a.blueEssence?.toLocaleString()}</td>
													<td style={styles.td}>{a.currentLevel}</td>
													<td style={styles.td}>{a.skinCount ?? (a.skins ? a.skins.length : 0)}</td>
													<td style={styles.td}>{a.championCount ?? (a.champions ? a.champions.length : 0)}</td>
													<td style={styles.td}>{a.rank || '-'}</td>
													<td style={{ ...styles.td, fontSize: 11, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.skinShardIds && a.skinShardIds.length > 0 ? a.skinShardIds.join(', ') : '-'}>{a.skinShardIds && a.skinShardIds.length > 0 ? a.skinShardIds.join(', ') : '-'}</td>
													<td style={{ ...styles.td, fontSize: 12 }}>{formatDate(a.createdAt)}</td>
													<td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
														<button style={styles.btnEdit} onClick={() => openEditAccount(a)}>
															Edit
														</button>
														<button style={styles.btnDanger} onClick={() => deleteAccount(a._id, a.summonerName)}>
															Delete
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>

						{/* Edit Account Modal */}
						{editingAccount && (
							<div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => { if (e.target === e.currentTarget) setEditingAccount(null); }}>
								<div style={{ ...styles.card, maxWidth: 700, width: '90%', maxHeight: '90vh', overflowY: 'auto', margin: 0 }}>
									<h2 style={{ fontSize: 20, marginBottom: 16, color: '#fff' }}>Edit Account: {editingAccount.summonerName}</h2>
									<form onSubmit={submitEditAccount}>
										<div style={styles.row}>
											<div style={styles.col}>
												<label style={styles.label}>Summoner Name</label>
												<input style={styles.input} name='summonerName' value={editAccountForm.summonerName} onChange={handleEditAccountChange} required />
											</div>
											<div style={styles.col}>
												<label style={styles.label}>Password (leave empty to keep current)</label>
												<input style={styles.input} name='password' type='text' value={editAccountForm.password} onChange={handleEditAccountChange} placeholder='Leave empty to keep' />
											</div>
										</div>
										<div style={styles.row}>
											<div style={styles.col}>
												<label style={styles.label}>ID Verbose</label>
												<select style={styles.select} name='idVerbose' value={editAccountForm.idVerbose} onChange={handleEditAccountChange}>
													<option value=''>-- None --</option>
													{products.map((p) => (<option key={p._id} value={p.idVerbose}>{p.idVerbose} - {p.name}</option>))}
												</select>
											</div>
											<div style={styles.col}>
												<label style={styles.label}>Region</label>
												<select style={styles.select} name='region' value={editAccountForm.region} onChange={handleEditAccountChange}>
													{['br', 'las', 'lan', 'na', 'euw', 'eune', 'jp', 'oce', 'kr', 'tr', 'ru'].map((r) => (<option key={r} value={r}>{r.toUpperCase()}</option>))}
												</select>
											</div>
											<div style={styles.col}>
												<label style={styles.label}>Status</label>
												<select style={styles.select} name='stockStatus' value={editAccountForm.stockStatus} onChange={handleEditAccountChange}>
													<option value='in-stock'>In Stock</option>
													<option value='sold'>Sold</option>
													<option value='banned'>Banned</option>
												</select>
											</div>
										</div>
										<div style={styles.row}>
											<div style={styles.col}>
												<label style={styles.label}>Blue Essence</label>
												<input style={styles.input} name='blueEssence' type='number' value={editAccountForm.blueEssence} onChange={handleEditAccountChange} />
											</div>
											<div style={styles.col}>
												<label style={styles.label}>Level</label>
												<input style={styles.input} name='currentLevel' type='number' value={editAccountForm.currentLevel} onChange={handleEditAccountChange} />
											</div>
											<div style={styles.col}>
												<label style={styles.label}>Capsule Type</label>
												<select style={styles.select} name='capsuleType' value={editAccountForm.capsuleType} onChange={handleEditAccountChange}>
													<option value='be'>Blue Essence</option>
													<option value='capsule'>Capsule</option>
												</select>
											</div>
										</div>
										<div style={styles.row}>
											<div style={styles.col}>
												<label style={styles.label}>Skin Count</label>
												<input style={styles.input} name='skinCount' type='number' value={editAccountForm.skinCount} onChange={handleEditAccountChange} />
											</div>
											<div style={styles.col}>
												<label style={styles.label}>Champion Count</label>
												<input style={styles.input} name='championCount' type='number' value={editAccountForm.championCount} onChange={handleEditAccountChange} />
											</div>
											<div style={styles.col}>
												<label style={styles.label}>Rank</label>
												<input style={styles.input} name='rank' value={editAccountForm.rank} onChange={handleEditAccountChange} placeholder='e.g. oro, plata' />
											</div>
										</div>
										<div style={styles.row}>
											<div style={styles.col}>
												<label style={styles.label}>Email</label>
												<input style={styles.input} name='email' value={editAccountForm.email} onChange={handleEditAccountChange} />
											</div>
											<div style={styles.col}>
												<label style={styles.label}>Birthdate</label>
												<input style={styles.input} name='birthdate' value={editAccountForm.birthdate} onChange={handleEditAccountChange} />
											</div>
										</div>
										<div>
											<label style={styles.label}>Skin Shard IDs (comma-separated)</label>
											<input style={styles.input} name='skinShardIds' value={editAccountForm.skinShardIds} onChange={handleEditAccountChange} placeholder='e.g. 15, 56, 32' />
										</div>
										<div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
											<button style={styles.btnPrimary} type='submit' disabled={editAccountLoading}>
												{editAccountLoading ? 'Saving...' : 'Save Changes'}
											</button>
											<button style={styles.btnSecondary} type='button' onClick={() => setEditingAccount(null)}>
												Cancel
											</button>
										</div>
									</form>
								</div>
							</div>
						)}

						{/* Pagination */}
						{stockPagination.totalPages > 1 && (
							<div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
								<button
									style={styles.btnSecondary}
									disabled={stockPagination.page <= 1}
									onClick={() => handleStockPageChange(1)}
								>
									First
								</button>
								<button
									style={styles.btnSecondary}
									disabled={stockPagination.page <= 1}
									onClick={() => handleStockPageChange(stockPagination.page - 1)}
								>
									Prev
								</button>
								<span style={{ padding: '6px 14px', color: '#aaa' }}>
									Page {stockPagination.page} of {stockPagination.totalPages}
								</span>
								<button
									style={styles.btnSecondary}
									disabled={stockPagination.page >= stockPagination.totalPages}
									onClick={() => handleStockPageChange(stockPagination.page + 1)}
								>
									Next
								</button>
								<button
									style={styles.btnSecondary}
									disabled={stockPagination.page >= stockPagination.totalPages}
									onClick={() => handleStockPageChange(stockPagination.totalPages)}
								>
									Last
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default AdminPanel;
